/**
 * batch-50 accountant-rol (FO-404) - de vergrendelde ACCOUNTANT-allowlist.
 *
 * De rol ACCOUNTANT is een HARD begrensd alleen-lezen profiel: uitsluitend
 * `FACTUREN.canView`, `INKOOP.canView` en `DECLARATIES.canView`. Deze module is
 * de ENIGE bron van die grens; API (`requirePermission` /
 * `getModulePermissions`), de rechtenmatrix-schrijfpaden en de backoffice-UI
 * lezen allemaal hiervandaan.
 *
 * Twee eigenschappen die je nooit mag omdraaien:
 *
 *  1. **De klem neemt alleen weg, hij geeft nooit.** `klemAccountantVlaggen`
 *     zet elke niet-toegestane vlag op `false` en laat een toegestane vlag op
 *     zijn opgeslagen waarde staan. De `ModulePermission`-tabel blijft dus de
 *     single source of truth (een ontbrekende rij = geen recht); de allowlist
 *     is een bovengrens, geen grant.
 *  2. **De klem draait VOOR de vlag-checks.** Een per-ongeluk-override in de
 *     rechtenmatrix kan daardoor niets openen - defense-in-depth naast de
 *     422-weigering op het schrijfpad.
 *
 * Payroll/loonstroken vallen expliciet BUITEN de allowlist (spec §4 + A3);
 * uitbreiden is een bewuste PO-beslissing plus een review, geen zijeffect.
 */

/** De vijf permissie-vlaggen op een `ModulePermission`-rij (vaste volgorde). */
export const ACCOUNTANT_PERMISSIE_VLAGGEN = [
  'canView',
  'canEdit',
  'canDelete',
  'canApprove',
  'canInvoice',
] as const

export type AccountantPermissieVlag = (typeof ACCOUNTANT_PERMISSIE_VLAGGEN)[number]

/** De rolnaam zoals in de Prisma `UserRole`-enum. */
export const ACCOUNTANT_ROL = 'ACCOUNTANT'

/**
 * De volledige allowlist: module -> toegestane vlaggen. Alles wat hier niet in
 * staat is voor een ACCOUNTANT hard `false`, ongeacht de rechtenmatrix.
 */
export const ACCOUNTANT_ALLOWLIST: Readonly<
  Record<string, ReadonlyArray<AccountantPermissieVlag>>
> = {
  FACTUREN: ['canView'],
  INKOOP: ['canView'],
  DECLARATIES: ['canView'],
}

/** De drie modules die een ACCOUNTANT mag zien, in navigatie-volgorde. */
export const ACCOUNTANT_MODULES = ['FACTUREN', 'INKOOP', 'DECLARATIES'] as const

export type AccountantModule = (typeof ACCOUNTANT_MODULES)[number]

/**
 * Verkoopfactuur-statussen die een ACCOUNTANT mag zien (spec §6.3): alleen
 * definitief/verzonden werk. Concepten (CONCEPT/GEREED) en de klant-akkoord-
 * fase (VERSTUURD_KLANT/AKKOORD_KLANT/AFGEKEURD_KLANT) zijn intern werk en
 * blijven buiten beeld - server-side `where`, geen UI-filter.
 */
export const ACCOUNTANT_ZICHTBARE_FACTUUR_STATUSSEN = [
  'DEFINITIEF',
  'VERZONDEN',
  'BETAALD',
  'VERZONDEN_NAAR_FINQLE',
  'IN_BEHANDELING_FINQLE',
  'GEFACTUREERD',
] as const

export type AccountantZichtbareFactuurStatus =
  (typeof ACCOUNTANT_ZICHTBARE_FACTUUR_STATUSSEN)[number]

/** Is dit de vergrendelde accountant-rol? Tolerant voor `null`/`undefined`. */
export function isAccountantRol(role: string | null | undefined): boolean {
  return role === ACCOUNTANT_ROL
}

/** Mag een ACCOUNTANT deze (module, vlag)-combinatie überhaupt hebben? */
export function accountantVlagToegestaan(module: string, vlag: string): boolean {
  const toegestaan = ACCOUNTANT_ALLOWLIST[module]
  if (!toegestaan) return false
  return (toegestaan as ReadonlyArray<string>).includes(vlag)
}

/** Mag een ACCOUNTANT deze module überhaupt zien? */
export function accountantModuleToegestaan(module: string): boolean {
  return ACCOUNTANT_ALLOWLIST[module] !== undefined
}

/** Minimale vorm van een permissie-rij waar de klem op werkt. */
export interface AccountantKlembareRij {
  module: string
  canView: boolean
  canEdit: boolean
  canDelete: boolean
  canApprove: boolean
  canInvoice: boolean
}

/**
 * Klem één permissie-rij naar de allowlist. Nooit een vlag aanzetten die er
 * niet stond; wel elke niet-toegestane vlag uitzetten. Retourneert de rij
 * ongewijzigd (referentie-identiek) als er niets te klemmen viel, zodat de
 * cache geen onnodige kopieën maakt.
 */
export function klemAccountantVlaggen<T extends AccountantKlembareRij>(rij: T): T {
  let gewijzigd = false
  const geklemd: AccountantKlembareRij = {
    module: rij.module,
    canView: rij.canView,
    canEdit: rij.canEdit,
    canDelete: rij.canDelete,
    canApprove: rij.canApprove,
    canInvoice: rij.canInvoice,
  }
  for (const vlag of ACCOUNTANT_PERMISSIE_VLAGGEN) {
    if (geklemd[vlag] && !accountantVlagToegestaan(rij.module, vlag)) {
      geklemd[vlag] = false
      gewijzigd = true
    }
  }
  return gewijzigd ? { ...rij, ...geklemd } : rij
}

/**
 * Klem een hele rijenset. Voor elke andere rol een no-op (zelfde array-
 * referentie), zodat het hete permissie-pad niets extra's doet.
 */
export function klemAccountantPermissies<T extends AccountantKlembareRij>(
  role: string | null | undefined,
  rijen: T[],
): T[] {
  if (!isAccountantRol(role)) return rijen
  return rijen.map((r) => klemAccountantVlaggen(r))
}

/**
 * Levert de (module, vlag)-paren uit een gewenste permissie-set die voor een
 * ACCOUNTANT niet mogen. Leeg = de set past binnen de allowlist. Wordt gebruikt
 * door de schrijfpaden (rechtenmatrix/medewerker-detail) om met 422 te
 * weigeren i.p.v. stil te klemmen.
 */
export function accountantOvertredingen(
  gewenst: ReadonlyArray<AccountantKlembareRij>,
): Array<{ module: string; vlag: AccountantPermissieVlag }> {
  const out: Array<{ module: string; vlag: AccountantPermissieVlag }> = []
  for (const rij of gewenst) {
    for (const vlag of ACCOUNTANT_PERMISSIE_VLAGGEN) {
      if (rij[vlag] && !accountantVlagToegestaan(rij.module, vlag)) {
        out.push({ module: rij.module, vlag })
      }
    }
  }
  return out
}

/**
 * batch-52 accountant-rol (FO-404 §7) — één regel uit de inzage-audit, zoals
 * `GET /employees/:id/inzage-audit` hem levert.
 *
 * Bewust GEEN documentinhoud: alleen wie/wat/wanneer plus het soort document en
 * een korte context (bestandsnaam of bundel-omvang).
 */
export interface AccountantInzageRegel {
  id: string
  /** ISO-8601 (UTC); de UI formatteert in Europe/Amsterdam. */
  tijdstip: string
  /** VERKOOPFACTUUR / INKOOPFACTUUR / DECLARATIE / EXPORT. */
  gegevenstype: string
  betreftObjectType: string
  betreftObjectId: string
  /** Bestandsnaam of bundel-omvang; nooit inhoud. */
  context: string | null
  /** download / preview / export. */
  bron: string
}

/** Nette, uitlegbare foutmelding bij een geweigerde override (422). */
export const ACCOUNTANT_ALLOWLIST_MESSAGE =
  'De rol Accountant heeft een vast profiel: alleen inzage in Verkoopfacturen, ' +
  'Inkoopfacturen en Declaraties. Rechten daarbuiten zijn niet toe te kennen.'

/** Foutcode die de frontend herkent om de vergrendelde matrix te tonen. */
export const ACCOUNTANT_ALLOWLIST_CODE = 'ACCOUNTANT_ALLOWLIST_LOCKED'
